import {
  Box,
  Button,
  chakra,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Heading,
  Input,
  Tooltip,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { type FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from 'assets/icons/Info.svg';
import { SectionLoading } from 'components';
import { defaultInputContainerStyles, InputContainer } from 'components/InputContainer';
import { byteToGigaByte, gigaByteToByte } from 'utils';
import type {
  CompanyPoolingConfigQuery,
  CreateCompanyPoolingConfigMutationVariables,
} from 'utils/graphql/hooks';
import {
  useCompanyPoolingConfigQuery,
  useCreateCompanyPoolingConfigMutation,
  UserGroup,
} from 'utils/graphql/hooks';
import { useIotSimToast } from 'utils/hooks';
import { useAuthUserContext } from 'utils/provider/AuthUserProvider';

interface SystemAdminConfigContentProps {
  poolingConfig?: CompanyPoolingConfigQuery['companyPoolingConfig'];
}

const SystemAdminConfigContent: FunctionComponent<SystemAdminConfigContentProps> = ({
  poolingConfig,
}) => {
  const { t } = useTranslation();
  const { hasRequiredRole } = useAuthUserContext();
  const successToast = useIotSimToast({ status: 'success' });
  const errorToast = useIotSimToast({ status: 'error' });
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useCreateCompanyPoolingConfigMutation();

  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
  } = useForm<CreateCompanyPoolingConfigMutationVariables>({
    defaultValues: {
      validFrom: '',
      dataVolumeLimit: byteToGigaByte(poolingConfig?.dataVolumeLimit ?? 0),
      smsVolumeLimit: poolingConfig?.smsVolumeLimit,
    },
  });
  const isSubmitDisabled = !hasRequiredRole(UserGroup.SystemAdmin) || isPending || !isDirty;

  const onSubmit = handleSubmit(async ({ dataVolumeLimit }) => {
    try {
      if (dataVolumeLimit) {
        await mutateAsync({
          dataVolumeLimit: gigaByteToByte(dataVolumeLimit),

          // no manual adjustment for those values right now:
          validFrom: new Date().toISOString(),
          smsVolumeLimit: poolingConfig?.smsVolumeLimit,
        });

        successToast({ title: t('common.updateAttributesSuccess') });
        queryClient.invalidateQueries({ queryKey: ['CompanyPoolingConfig'] });
      }
    } catch (e) {
      errorToast({
        title: (e instanceof Error && t('common.updateAttributesError')) || t('error.generic'),
        ...(e instanceof Error && { description: e.message }),
      });
    }
  });

  return (
    <>
      <Heading as="h2" size="md" px="12" pt="7" display="flex" justifyContent="space-between">
        {t('account.company.systemAdminConfig.heading')}
      </Heading>
      <chakra.form m={[null, 6]} my={[6, null]} onSubmit={onSubmit}>
        <Grid templateColumns={['1fr', null, '1fr 1fr']} pb="4">
          <InputContainer>
            <FormControl isInvalid={!!errors.dataVolumeLimit}>
              <Flex>
                <FormLabel mr="2">
                  {t('account.company.systemAdminConfig.dataVolumePoolingLimit')}
                </FormLabel>
                <Tooltip
                  label={t('account.company.systemAdminConfig.dataVolumePoolingLimitDisableHint')}
                >
                  <Box mb="2" alignSelf="center">
                    <InfoIcon />
                  </Box>
                </Tooltip>
              </Flex>
              <Input
                {...register('dataVolumeLimit', {
                  validate: (value) =>
                    value && value >= 0 && value < byteToGigaByte(Number.MAX_SAFE_INTEGER)
                      ? true
                      : t('account.company.systemAdminConfig.error.invalidLimit'),
                })}
                size="sm"
                type="number"
                variant="userData"
              />
              <FormErrorMessage whiteSpace="pre-line">
                {errors.dataVolumeLimit?.message}
              </FormErrorMessage>
            </FormControl>
          </InputContainer>
        </Grid>
        <Button
          type="submit"
          isDisabled={isSubmitDisabled}
          isLoading={isPending}
          loadingText={t('common.submitting')}
          mx={defaultInputContainerStyles.mx}
        >
          {t('common.submit')}
        </Button>
      </chakra.form>
    </>
  );
};

export const SystemAdminConfig: FunctionComponent = () => {
  const { t } = useTranslation();
  const errorToast = useIotSimToast({ status: 'error' });

  const { data, isLoading, isError, error } = useCompanyPoolingConfigQuery({}, { retry: false });

  if (isLoading) {
    return <SectionLoading />;
  }

  if (isError) {
    errorToast({
      title: t('account.company.systemAdminConfig.error.loadingExistingConfigFails'),
      ...(error instanceof Error && { description: error.message }),
    });
  }

  return <SystemAdminConfigContent poolingConfig={data?.companyPoolingConfig} />;
};
